import React, { useState, useEffect } from "react";
import { BrandLogo } from "../components/brand/BrandLogo";
import { useNavigate } from "react-router-dom";
import { Building2, KeyRound, ShieldCheck, Wrench, ArrowRight, Briefcase, Users, Landmark, ClipboardCheck, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ICONS = {
  bailleur: Building2, locataire: KeyRound, agent: Briefcase, gestionnaire: Users,
  assureur: ShieldCheck, artisan: Wrench, courtier: Landmark, referent: ClipboardCheck,
};
const DESCS = {
  bailleur: "Gérez vos biens, locataires, loyers et finances.",
  locataire: "Accédez à vos documents et souscrivez une assurance.",
  agent: "Gérez vos mandats et vos biens en portefeuille.",
  gestionnaire: "Administrez les biens de vos clients au quotidien.",
  assureur: "Publiez vos produits d'assurance et suivez les souscriptions.",
  artisan: "Créez votre profil et recevez des demandes de travaux.",
  courtier: "Accompagnez vos clients dans leur financement.",
  referent: "Réalisez les états des lieux confiés par les propriétaires.",
};

export default function RoleSelection() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [saving, setSaving] = useState(null);
  const [profiles, setProfiles] = useState(null);

  useEffect(() => {
    api.get("/auth/signup-profiles").then((r) => setProfiles(r.data || [])).catch(() => setProfiles([]));
  }, []);

  const choose = async (role) => {
    setSaving(role);
    try {
      const res = await api.put("/auth/role", { role });
      setUser(res.data);
      navigate("/app", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur, réessayez");
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-brand-softer flex flex-col items-center justify-center px-5 py-12">
      <div className="flex items-center gap-2 mb-3">
        <BrandLogo className="h-8 sm:h-9 w-auto" />
      </div>
      <h1 className="font-display text-4xl font-semibold text-brand-greenDeep text-center">Bienvenue !</h1>
      <p className="text-brand-green/60 mt-2 mb-3 text-center">Choisissez votre profil pour personnaliser votre espace.</p>
      <p className="text-xs text-brand-green/45 mb-8 text-center max-w-md">Ce choix est définitif : seul un administrateur pourra le modifier ensuite.</p>

      {profiles === null ? (
        <Loader2 className="w-7 h-7 animate-spin text-brand-limeDark" />
      ) : profiles.length === 0 ? (
        <p className="text-brand-green/60">Aucun profil disponible pour le moment. Contactez l'administrateur.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5 w-full max-w-3xl" data-testid="role-grid">
          {profiles.map((r) => {
            const Icon = ICONS[r.role] || Building2;
            return (
              <button
                key={r.role}
                onClick={() => choose(r.role)}
                disabled={!!saving}
                data-testid={`role-option-${r.role}`}
                className="group text-left bg-white rounded-2xl p-6 border border-brand-soft hover:border-brand-lime hover:shadow-xl transition-all disabled:opacity-60"
              >
                <span className="w-12 h-12 rounded-xl bg-brand-soft group-hover:bg-brand-lime flex items-center justify-center transition-colors mb-4">
                  <Icon className="w-6 h-6 text-brand-greenDeep" />
                </span>
                <h3 className="font-semibold text-lg text-brand-greenDeep flex items-center gap-2">
                  {r.label}
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </h3>
                <p className="text-sm text-brand-green/60 mt-1">{DESCS[r.role] || ""}</p>
                {saving === r.role && <p className="text-xs text-brand-limeDark mt-2">Configuration...</p>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

